
import { Vue, Component } from 'vue-property-decorator';
import { Mutation, State, Getter, Action } from 'vuex-class';
import apis from '@/const/apis';
import axios from '@/utils/axios';
import HelpCenter from '@/components/help-center.vue';
import ProgressBar from '@/components/progress-bar.vue';
import Highlight from '@/components/highlight.vue';
import { toLocaleNumber } from '@/mixins/currencyMixin';
import { CurrentBillStatus, TeaConfigPayment, paymethodTypeMap } from '@/const/payment';
import { SET_CREDIT_INFO } from '@/store/types';
import { getLang } from '@/utils/unicodeUtil';
import store from '@/store/index.ts';
import BytedIconAttention from '@byte-design/vue-icons/icons/Attention';
import BytedIconMore from '@byte-design/vue-icons/icons/More';
import BytedIconRight from '@byte-design/vue-icons/icons/Right';
import PopupHelp from './popup-help.vue';
import BytedIconHelp from '@byte-design/vue-icons/icons/Help';
import { getRiskInfo } from '@/utils/tool';
import { getAbVersionByName, LibraTestName, LibraTestAbVersionResult } from '@/AbtestVersion';
import { domainTTP, PAYMENT_SCENE } from '@/const/common';
import qs from 'qs';

interface HighLightOpt {
  id: string;
  el: HTMLElement | null;
  'store-key': string;
  hint: string;
}

const HighLightOptMap: { [key: string]: HighLightOpt } = {
  due: {
    id: 'due_highlight',
    el: null,
    'store-key': 'credit_payment_due',
    hint: 'module_purchase_hit_due',
  },
  bill: {
    id: 'bill_highlight',
    el: null,
    'store-key': 'credit_payment_bill',
    hint: 'module_purchase_hit_bill',
  },
  manage: {
    id: 'manage_highlight',
    el: null,
    'store-key': 'credit_payment_manage',
    hint: 'module_purchase_hit_manage',
  },
};
const HighLightSeq = ['due', 'bill', 'manage'];

@Component({
  name: 'CreditPayment',
  components: {
    HelpCenter,
    ProgressBar,
    Highlight,
    BytedIconAttention,
    BytedIconMore,
    BytedIconRight,
    PopupHelp,
    BytedIconHelp,
  },
})
export default class CreditPayment extends Vue {
  @State('cardInfo', { namespace: 'payment' }) cardInfo: any;
  @State('payNowABTest') payNowABTest: any;
  @Mutation(`payment/${SET_CREDIT_INFO}`) setCreditInfo: any;
  @Mutation('SET_PAYNOW_ABTEST') setPaynowAbtest: any;
  @Getter('payment/creditInfo') creditInfo: any;
  @Getter('payment/manageUrl') manageUrl: string; // 管理信用卡URL
  @Getter('payment/totalCards') totalCards: number;
  @Getter('payment/isActivePaSDK') isActivePaSDK: boolean;
  @Action('payment/getCardInfo') getCardInfo: () => Promise<{
    identity: string;
    total: number;
  }>;
  @Action('payment/getCardBindLink') getCardBindLink: () => Promise<string>;
  @Action('payment/getPaymentAccount') getPaymentAccount: () => Promise<string>;

  showHelp = false;
  paymethodTypeMap = paymethodTypeMap;
  private showManagerCard = false; // 是否展示卡管理
  private showFlag = false; // 是否展示highlight
  private highLightSeq: string[] = HighLightSeq; // highlight 元素顺序
  private highLightCursor = 0; // 高亮展示元素指针
  private highLightOpt: HighLightOpt = {
    id: '',
    el: null,
    'store-key': '',
    hint: '',
  };

  beforeRouteEnter(to: any, from: any, next: any) {
    Promise.all([
      store.dispatch('payment/getChargeInfo'),
      store.dispatch('payment/getCardInfo'),
      store.dispatch('payment/getPaymentAccount'),
    ])
      .then(() => {
        // 是否激活pa
        const isActivePaSDK = store.getters['payment/isActivePaSDK'];

        const isPrepay = store.getters['payment/isPrepay'];
        const isPostpay = store.getters['payment/isPostpay'];
        const totalCards = store.getters['payment/totalCards'];
        const aadvid = to.query?.aadivd;

        const query = qs.stringify({
          _pia_: 1,
          adv_id: aadvid,
          platform: 8,
          ...to.query,
        });

        if (isActivePaSDK) {
          // platform 8 标记ttam h5
          window.location.href = `${location.origin}/wap/pa/credit_purchase?${query}`;
          return;
        }

        if (isPrepay) {
          next({ name: 'purchase', query: { aadvid } });
          return;
        }
        if (!isPostpay) {
          next({ name: 'perf-dashboard-detail', query: { aadvid } });
          return;
        }
        if (isPostpay && totalCards <= 0) {
          // 收银台线上绑卡验证多次跳转其他平台之后会丢失事先携带的query，导致lite侧只能针对各种情况做处理，这里不能改动，否则会出问题
          next({ name: 'setting', query: { aadvid } });
          return;
        }
        next();
      })
      .catch(() => {
        next({ name: 'setting', query: to.query });
      });
  }

  // 当前累积未出账消耗
  get creditCost(): number {
    const creditCost = Number(this.creditInfo?.credit_cost ?? '');
    return !isNaN(creditCost) ? creditCost : 0;
  }
  get formatedCreditCost() {
    return toLocaleNumber(this.creditCost);
  }
  // 当前使用的货币
  get currency(): string {
    return this.creditInfo?.currency ?? '';
  }
  // 账单额度
  get billBalance(): number {
    const billBalance = Number(this.creditInfo?.bill_balance ?? '');
    return !isNaN(billBalance) ? billBalance : 0;
  }
  get formatedBillBalance() {
    return toLocaleNumber(this.billBalance);
  }
  // 可用余额
  get availableBalance() {
    const available = this.billBalance - this.creditCost;
    return toLocaleNumber(available);
  }
  // 进度条 百分比
  get usedPercent(): number {
    return this.billBalance ? Math.floor((this.creditCost / this.billBalance) * 100) : 0;
  }
  // 下次账单时间
  get nextBillTime(): number {
    return (this.creditInfo?.next_bill_time ?? 0) * 1000;
  }
  // 下次账单月份
  get nextBillMonth(): number {
    return new Date(this.nextBillTime).getMonth() + 1;
  }
  // 下次账单日
  get nextBillDate(): number {
    return new Date(this.nextBillTime).getDate();
  }

  get queryParam(): string {
    return `&language=${getLang()}&newCreate=${
      Number(this.$route.query?.newCreate) ? 1 : 0
    }&redirect=${encodeURIComponent('/wap/credit_purchase')}`;
  }
  // 当前账单的状态
  get currentBillStatus(): number {
    return this.creditInfo?.current_bill?.status;
  }
  // 当前账单是否支付失败
  get isCurrentPayFailed(): boolean {
    return this.currentBillStatus === CurrentBillStatus.Fail || Boolean(this.creditInfo?.invoice_id?.length);
  }
  // 主卡卡号
  get primaryCardIdentity(): string {
    return this.cardInfo?.identity ?? '';
  }
  // 主卡过期时间
  get primaryCardExpire(): string {
    return this.cardInfo?.expire_date ?? '';
  }

  get cardLogoUrl(): string {
    return this.cardInfo?.card_logo_url ?? '';
  }

  created() {
    this.initData();
  }

  mounted() {
    this.renderHighLight();
  }

  async initData() {
    if (!this.payNowABTest.load) {
      try {
        const result = await getAbVersionByName(LibraTestName.PAYNOW_API);
        this.setPaynowAbtest(result?.group === LibraTestAbVersionResult.treatment);
      } catch (e) {
        this.setPaynowAbtest(false);
      }
    }
    // 获取 credit info
    if (this.payNowABTest.load && !this.payNowABTest.isAbTest) {
      axios.get(apis.payment.getCreditInfo).then(res => {
        let { data = {} } = res || {};
        this.setCreditInfo(data);
      });
    } else {
      if (!this.creditInfo?.invoice_id || !this.creditInfo?.invoice_id?.length) {
        const currentDomain = window.location.host;
        const scene = currentDomain === domainTTP ? PAYMENT_SCENE.TTP_MOBILE : PAYMENT_SCENE.DEFAULT;
        Promise.all([
          axios.get(apis.payment.getCreditInfo),
          axios.post(apis.payment.getUnpaidInvoice, { ad_channel: 'TTAM_MOBILE_LITE', ...getRiskInfo(), scene }),
        ]).then(res => {
          let [{ data: creditInfo = {} }, { data: unpaidInfo = {} }] = res || [{}, {}];
          const data = {
            ...creditInfo,
            tax_detail: JSON.parse(unpaidInfo?.invoice_entity?.tax_detail_map || '{}'),
            amount: unpaidInfo?.invoice_entity?.unpaid_amount,
            balance: unpaidInfo?.invoice_entity?.unpaid_amount_without_tax,
            tax_rate_map: JSON.parse(unpaidInfo?.invoice_entity?.tax_rate_map || '{}'),
            status: unpaidInfo?.invoice_entity?.status,
            invoice_id: unpaidInfo?.invoice_entity?.invoice_id_list,
            tax: unpaidInfo?.invoice_entity?.unpaid_total_tax_amount,
            currency: unpaidInfo?.invoice_entity?.currency,
          };
          this.setCreditInfo(data);
        });
      }
    }
    // 获取 绑卡链接
    this.getCardBindLink();
  }

  goManageCard(source: string) {
    if (source === 'more') {
      this.$trackEvent(TeaConfigPayment.payment_lite_manage, {
        payment_type: 2,
      });
    } else if (source === 'card') {
      this.$trackEvent(TeaConfigPayment.payment_lite_autopay_manage2);
    }

    this.showManagerCard = false;
    window.location.href = `${this.manageUrl}${this.queryParam}`;
  }

  goCheckout() {
    this.$trackEvent(TeaConfigPayment.payment_lite_autopay_try_again);
    this.$router.push({
      name: 'checkout',
    });
  }

  goBack() {
    this.$trackEvent(TeaConfigPayment.payment_lite_cancel);
    this.$router.push({
      name: 'setting',
    });
  }

  renderNextHighLight() {
    this.highLightCursor += 1;
    this.showFlag = false;
    this.$nextTick().then(() => {
      this.renderHighLight();
    });
  }

  renderHighLight() {
    this.showFlag = true;
    let { highLightCursor, highLightSeq } = this;
    if (highLightCursor >= highLightSeq.length) {
      return;
    }
    this.$trackEvent(TeaConfigPayment.payment_lite_autopay_guide_tip, {
      payment_sourse_guide_area: this.highLightCursor + 1,
    });
    const options = HighLightOptMap[highLightSeq[highLightCursor]];
    options.el = document.querySelector(`#${options.id}`);
    this.highLightOpt = options;
    this.$publish('highlight-rerender');
  }
  showManageCard() {
    this.showManagerCard = true;
    this.$trackEvent(TeaConfigPayment.payment_lite_more, {
      payment_type: 2,
    });
  }
  hideManageCard() {
    this.showManagerCard = false;
    this.$trackEvent(TeaConfigPayment.payment_lite_manage_cancel, {
      payment_type: 2,
    });
  }
  reportGoHelp() {
    this.$trackEvent(TeaConfigPayment.payment_lite_help, {
      payment_type: 2,
    });
  }
}
